import { supabase } from '../supabaseClient';

const defaultPreferences = {
  primaryGoal: '',
  bodyFat: '',
  mealCount: null,
  proteins: [],
  carbs: [],
  fats: [],
};

const toArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') {
    return value
      .split(/[\n,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const parseAssessmentValue = (value) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      return JSON.parse(value);
    } catch {
      return {};
    }
  }
  return value;
};

const normalizePreferences = (assessment = {}) => {
  const goals = assessment.goals || {};
  const nutrition = assessment.nutrition || {};
  const toNumber = (val) => {
    const num = Number(val);
    return Number.isFinite(num) ? num : null;
  };
  return {
    primaryGoal: goals.primary || '',
    bodyFat: goals.body_fat || goals.bodyfat || '',
    mealCount: toNumber(nutrition.meals_per_day_hint),
    proteins: Array.isArray(nutrition.proteins) ? nutrition.proteins : [],
    carbs: Array.isArray(nutrition.carbs) ? nutrition.carbs : [],
    fats: Array.isArray(nutrition.fats) ? nutrition.fats : [],
  };
};

export async function fetchMealPreferences() {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) throw new Error('Not authenticated');
  const { data, error } = await supabase
    .from('assessments')
    .select('assessment')
    .eq('user_id', userId)
    .maybeSingle();
  if (error) throw error;
  const assessment = parseAssessmentValue(data?.assessment);
  return { ...defaultPreferences, ...normalizePreferences(assessment) };
}

const mergeAssessmentPreferences = (prev = {}, updates = {}) => {
  const nutrition = prev.nutrition || {};
  const goals = prev.goals || {};
  return {
    ...prev,
    goals: {
      ...goals,
      primary: updates.primaryGoal ?? goals.primary ?? '',
    },
    nutrition: {
      ...nutrition,
      meals_per_day_hint: updates.mealCount ?? nutrition.meals_per_day_hint ?? null,
      proteins: updates.proteins ?? nutrition.proteins ?? [],
      carbs: updates.carbs ?? nutrition.carbs ?? [],
      fats: updates.fats ?? nutrition.fats ?? [],
    },
  };
};

export async function updateMealPreferences({
  primaryGoal = '',
  mealCount = null,
  proteins = [],
  carbs = [],
  fats = [],
}) {
  const { data: auth } = await supabase.auth.getUser();
  const userId = auth?.user?.id;
  if (!userId) throw new Error('Not authenticated');

  const { data: current } = await supabase
    .from('assessments')
    .select('assessment')
    .eq('user_id', userId)
    .maybeSingle();

  const prevAssessment = parseAssessmentValue(current?.assessment);
  const merged = mergeAssessmentPreferences(prevAssessment, {
    primaryGoal,
    mealCount: mealCount == null ? null : Number(mealCount),
    proteins: toArray(proteins),
    carbs: toArray(carbs),
    fats: toArray(fats),
  });

  const { error: upsertErr } = await supabase
    .from('assessments')
    .upsert({ user_id: userId, assessment: merged }, { onConflict: 'user_id' });
  if (upsertErr) throw upsertErr;

  try {
    const { error: nutritionErr } = await supabase
      .from('nutrition_preferences')
      .upsert({
        user_id: userId,
        proteins: toArray(proteins),
        carbs: toArray(carbs),
        fats: toArray(fats),
      }, { onConflict: 'user_id' });
    if (nutritionErr) throw nutritionErr;
  } catch (err) {
    // Some deployments might not include the optional nutrition_preferences table.
    console.warn('nutrition_preferences upsert skipped:', err?.message || err);
  }

  return { ...defaultPreferences, ...normalizePreferences(merged) };
}
